import { useEffect, useMemo, useRef, useState } from "react";
import { Ic, icons } from "./Icons";

// Captions hold at full opacity for CAPTION_HOLD around their trigger point,
// then fade out over the next CAPTION_FADE — a flat plateau, not just a
// single-instant peak, so there's real time to read before it fades.
const CAPTION_HOLD = 0.05;
const CAPTION_FADE = 0.08;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

function padFrameIndex(index, pad) {
  return String(index + 1).padStart(pad, "0");
}

// Sticky-pinned video whose playback position is driven by scroll progress
// through a tall track, not by autoplay. Captions cross-fade at fixed
// progress points; a permanent disclosure label is always rendered so it
// can never be designed away.
export default function ScrollScrubVideo({
  id,
  src,
  mobileSrc,
  poster,
  captions = [],
  disclosure,
  frames,
  trackHeightVh = 300,
  className = "",
}) {
  const trackRef = useRef(null);
  const stickyRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const captionRefs = useRef(new Map());

  const reducedMotion = useReducedMotion();
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [ready, setReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [useCanvas, setUseCanvas] = useState(false);

  const durationRef = useRef(0);
  const displayedProgressRef = useRef(0);
  const lastFrameIndexRef = useRef(-1);
  const framesLoadedRef = useRef([]);

  const resolvedSrc = useMemo(() => {
    if (typeof window !== "undefined" && mobileSrc && window.matchMedia("(max-width: 780px)").matches) {
      return mobileSrc;
    }
    return src;
  }, [src, mobileSrc]);

  // Pick the frame-sequence fallback on coarse-pointer (touch) devices when a
  // frame set is available — video.currentTime scrubbing is unreliable on
  // several mobile browsers.
  useEffect(() => {
    if (!frames || typeof window === "undefined") return;
    setUseCanvas(window.matchMedia("(pointer: coarse)").matches);
  }, [frames]);

  useEffect(() => {
    if (reducedMotion || typeof IntersectionObserver === "undefined" || !trackRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsNearViewport(entry.isIntersecting);
        if (entry.isIntersecting) setShouldLoad(true);
      },
      { rootMargin: "800px 0px 800px 0px", threshold: 0 }
    );
    observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, [reducedMotion]);

  // Preload the canvas frame sequence once the section is worth loading.
  useEffect(() => {
    if (!useCanvas || !frames || !shouldLoad || hasError) return;
    let cancelled = false;
    const { basePath, count, pad = 4, extension = "jpg" } = frames;
    const images = new Array(count);
    let loaded = 0;

    for (let i = 0; i < count; i += 1) {
      const img = new Image();
      img.onload = () => {
        loaded += 1;
        if (!cancelled && loaded === count) setReady(true);
      };
      img.onerror = () => {
        if (cancelled) return;
        console.error(`[ScrollScrubVideo#${id}] frame failed to load: ${img.src}`);
        setHasError(true);
      };
      img.src = `${basePath}/frame-${padFrameIndex(i, pad)}.${extension}`;
      images[i] = img;
    }

    framesLoadedRef.current = images;
    return () => {
      cancelled = true;
    };
  }, [useCanvas, frames, shouldLoad, hasError]);

  const applyFrame = (progress) => {
    if (useCanvas) {
      const images = framesLoadedRef.current;
      const canvas = canvasRef.current;
      if (!images.length || !canvas) return;
      const frameIndex = Math.round(progress * (images.length - 1));
      if (frameIndex === lastFrameIndexRef.current) return;
      lastFrameIndexRef.current = frameIndex;
      const img = images[frameIndex];
      if (!img || !img.complete) return;
      const ctx = canvas.getContext("2d");
      const { width, height } = canvas;
      const scale = Math.max(width / img.width, height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      ctx.drawImage(img, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
      return;
    }

    const video = videoRef.current;
    if (!video || !durationRef.current) return;
    const target = progress * durationRef.current;
    if (Math.abs(video.currentTime - target) > 0.03 && !video.seeking) {
      video.currentTime = target;
    }
  };

  const applyCaptions = (progress) => {
    captionRefs.current.forEach((node, at) => {
      if (!node) return;
      const distance = Math.abs(progress - at);
      const opacity = distance <= CAPTION_HOLD ? 1 : clamp(1 - (distance - CAPTION_HOLD) / CAPTION_FADE, 0, 1);
      node.style.opacity = String(opacity);
      node.style.transform = `translateY(${(1 - opacity) * 10}px)`;
    });
  };

  useEffect(() => {
    if (reducedMotion || !isNearViewport || !ready || hasError) return undefined;
    let rafId;

    const tick = () => {
      const track = trackRef.current;
      const sticky = stickyRef.current;
      if (!track || !sticky) return;
      const rect = track.getBoundingClientRect();
      const stickyHeight = sticky.getBoundingClientRect().height;
      // Sticky's own height (not window.innerHeight) and CSS `top` offset
      // (non-zero — it sits below the fixed nav) both matter here: the pin
      // is active while rect.top runs from `topOffset` down to
      // `topOffset - scrollable`, not from 0. Using rect.top alone would
      // make progress hit 1 before the pin actually releases.
      const topOffset = parseFloat(getComputedStyle(sticky).top) || 0;
      const scrollable = rect.height - stickyHeight;
      const target = scrollable > 0 ? clamp((topOffset - rect.top) / scrollable, 0, 1) : 0;

      displayedProgressRef.current = target;
      applyFrame(target);
      applyCaptions(target);

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [reducedMotion, isNearViewport, ready, hasError, useCanvas]);

  const registerCaptionRef = (at) => (node) => {
    if (node) captionRefs.current.set(at, node);
    else captionRefs.current.delete(at);
  };

  if (reducedMotion) {
    return (
      <section id={id} className={`scrub-static ${className}`}>
        {!posterFailed && (
          <img
            src={poster}
            alt=""
            className="scrub-static-poster"
            loading="lazy"
            onError={() => {
              console.error(`[ScrollScrubVideo#${id}] poster failed to load: ${poster}`);
              setPosterFailed(true);
            }}
          />
        )}
        <div className="scrub-static-overlay">
          {captions.map((caption) => (
            <p key={caption.text} className="scrub-static-caption">
              {caption.icon && <Ic d={icons[caption.icon]} size={18} color="#c4b5fd" />}
              <span>{caption.text}</span>
            </p>
          ))}
        </div>
        <div className="scrub-disclosure">{disclosure}</div>
      </section>
    );
  }

  return (
    <section id={id} ref={trackRef} className={`scrub-track ${className}`} style={{ height: `${trackHeightVh}vh` }}>
      <div className="scrub-sticky" ref={stickyRef}>
        {!posterFailed && (
          <img
            src={poster}
            alt=""
            className={`scrub-poster ${ready && !hasError ? "scrub-poster-hidden" : ""}`}
            onError={() => {
              console.error(`[ScrollScrubVideo#${id}] poster failed to load: ${poster}`);
              setPosterFailed(true);
            }}
          />
        )}

        {!hasError && useCanvas && (
          <canvas ref={canvasRef} width={1280} height={720} className="scrub-canvas" />
        )}

        {!hasError && !useCanvas && (
          <video
            ref={videoRef}
            className="scrub-video"
            src={shouldLoad ? resolvedSrc : undefined}
            poster={posterFailed ? undefined : poster}
            muted
            playsInline
            preload={shouldLoad ? "auto" : "none"}
            onLoadedMetadata={(event) => {
              durationRef.current = event.currentTarget.duration || 0;
              setReady(true);
              console.info(`[ScrollScrubVideo#${id}] video ready — duration ${event.currentTarget.duration?.toFixed(2)}s`);
            }}
            onError={(event) => {
              console.error(
                `[ScrollScrubVideo#${id}] video failed to load: ${event.currentTarget.currentSrc || resolvedSrc}`,
                event.currentTarget.error
              );
              setHasError(true);
            }}
          />
        )}

        {hasError && posterFailed && (
          <p className="scrub-fallback-note">Content unavailable — check the console for details.</p>
        )}

        <div className="scrub-caption-layer">
          {captions.map((caption) => (
            <p key={caption.text} ref={registerCaptionRef(caption.at)} className="scrub-caption">
              {caption.icon && <Ic d={icons[caption.icon]} size={18} color="#c4b5fd" />}
              <span>{caption.text}</span>
            </p>
          ))}
        </div>

        <div className="scrub-disclosure">{disclosure}</div>
      </div>
    </section>
  );
}
